import { router, useForm, usePage } from "@inertiajs/react";
import { useEffect, useState } from "react";
import Header from "./Component/Header";

function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("id-ID", {
        year: "numeric",
        month: "long",
        day: "2-digit",
    }).format(date);
}

function getHistoryStatus(history) {
    if (history?.actual_end_date) return "Selesai";
    if (history?.target_end_date) {
        const target = new Date(history.target_end_date);
        if (!Number.isNaN(target.getTime()) && target < new Date()) {
            return "Kedaluwarsa";
        }
    }
    return "Aktif";
}

function formatCurrency(value) {
    const amount = Number(value);
    if (Number.isNaN(amount)) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
    }).format(amount);
}

function getCommissionStatusMeta(status) {
    if (status === 2) {
        return {
            label: "Dibayar",
            className:
                "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
        };
    }

    if (status === 1) {
        return {
            label: "Disetujui",
            className:
                "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        };
    }

    if (status === 3) {
        return {
            label: "Batal",
            className:
                "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
        };
    }

    return {
        label: "Menunggu",
        className:
            "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    };
}

export default function Profile({ memberProfile = null }) {
    const {
        auth,
        flash = {},
        momo_coin_balance: momoCoinBalance = "0",
        commissions: memberCommissions = [],
        commissionDate = "",
    } = usePage().props;
    const user = auth?.user ?? null;

    const initialName = user?.name ?? "";
    const initialEmail = user?.email ?? "";
    const initialPhone = memberProfile?.mem_phone ?? "";
    const initialMomoCode = memberProfile?.momo_code ?? "";
    const { data, setData, put, processing, errors, recentlySuccessful } = useForm({
        name: initialName,
        email: initialEmail,
        phone: initialPhone,
        momo_code: initialMomoCode,
    });
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [selectedCommissionDate, setSelectedCommissionDate] = useState(
        commissionDate || new Date().toISOString().slice(0, 10)
    );

    useEffect(() => {
        setData({
            name: initialName,
            email: initialEmail,
            phone: initialPhone,
            momo_code: initialMomoCode,
        });
    }, [initialName, initialEmail, initialPhone, initialMomoCode]);

    useEffect(() => {
        if (commissionDate) {
            setSelectedCommissionDate(commissionDate);
        }
    }, [commissionDate]);

    const fullName = user?.name?.trim() ? user.name : "-";
    const email = user?.email?.trim() ? user.email : "-";
    const phone = memberProfile?.mem_phone?.trim() ? memberProfile.mem_phone : "-";
    const momoCode = memberProfile?.momo_code?.trim() ? memberProfile.momo_code : "-";
    const memberCode = memberProfile?.mem_code?.trim() ? memberProfile.mem_code : "-";
    const bankName = memberProfile?.mem_bank_name?.trim() ? memberProfile.mem_bank_name : "-";
    const bankAccountName = memberProfile?.mem_bank_account_name?.trim() ? memberProfile.mem_bank_account_name : "-";
    const bankAccountNumber = memberProfile?.mem_bank_account_number?.trim() ? memberProfile.mem_bank_account_number : "-";
    const profilePhotoUrl = memberProfile?.mem_photo?.trim()
        ? memberProfile.mem_photo
        : "https://images.unsplash.com/photo-1659482633369-9fe69af50bfb?ixlib=rb-4.0.3&amp;ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&amp;auto=format&amp;fit=facearea&amp;facepad=3&amp;w=320&amp;h=320&amp;q=80";
    const hasMomoCode = Boolean((memberProfile?.momo_code ?? "").trim());
    const registrationMemberDate = formatDate(memberProfile?.created_at);
    const membershipStatus = memberProfile?.mem_id ? "Aktif" : "Tidak Aktif";
    const memberHistories = Array.isArray(memberProfile?.level_histories)
        ? memberProfile.level_histories
        : [];
    const commissions = Array.isArray(memberCommissions) ? memberCommissions : [];
    const totalCommissionAmount = commissions.reduce(
        (total, commission) => total + Number(commission?.commission_amount ?? 0),
        0
    );

    const handleProfileSubmit = (event) => {
        event.preventDefault();
        put("/profile", {
            preserveScroll: true,
            onSuccess: () => setIsEditingProfile(false),
        });
    };

    const handleCommissionDateChange = (event) => {
        const nextDate = event.target.value;
        setSelectedCommissionDate(nextDate);

        router.get(
            "/profile",
            { date: nextDate },
            {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            }
        );
    };


    return <>
        <Header />
        <div className="max-w-6xl mx-auto">
            <div className="p-2 sm:p-5 sm:py-0 md:pt-5 space-y-5">
                <div className="p-3 md:p-5 bg-white border border-gray-200 shadow-2xs rounded-xl dark:bg-neutral-800 dark:border-neutral-700">
                    <figure>
                        <svg className="w-full" preserveAspectRatio="none" width="1113" height="161" viewBox="0 0 1113 161" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_697_201879)">
                                <rect x="1" width="1112" height="348" fill="#B2E7FE"></rect>
                                <rect width="185.209" height="704.432" transform="matrix(0.50392 0.86375 -0.860909 0.508759 435.452 -177.87)" fill="#FF8F5D"></rect>
                                <rect width="184.653" height="378.667" transform="matrix(0.849839 -0.527043 0.522157 0.852849 -10.4556 -16.4521)" fill="#3ECEED"></rect>
                                <rect width="184.653" height="189.175" transform="matrix(0.849839 -0.527043 0.522157 0.852849 35.4456 58.5195)" fill="#4C48FF"></rect>
                            </g>
                            <defs>
                                <clipPath id="clip0_697_201879">
                                    <rect x="0.5" width="1112" height="161" rx="12" fill="white"></rect>
                                </clipPath>
                            </defs>
                        </svg>
                    </figure>
                    <div className="-mt-24">
                        <div className="relative flex size-30 mx-auto border-4 border-white rounded-full dark:border-neutral-800">
                            <img className="object-cover size-full rounded-full" src={profilePhotoUrl} alt={fullName} />

                            <div className="absolute bottom-0 -end-2">
                                <button type="button" className="group p-2 max-w-[125px] inline-flex justify-center items-center gap-x-2 text-start bg-red-600 border border-red-600 text-white text-xs font-medium rounded-full shadow-2xs align-middle focus:outline-hidden focus:bg-red-500" data-hs-overlay="#hs-pro-dsm" aria-expanded="false">
                                    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                                        <line x1="9" x2="9.01" y1="9" y2="9"></line>
                                        <line x1="15" x2="15.01" y1="9" y2="9"></line>
                                    </svg>
                                    <span className="group-hover:block hidden">Offline</span>
                                </button>
                            </div>
                        </div>

                        <div className="mt-3 text-center">
                            <h1 className="text-xl font-semibold text-gray-800 dark:text-neutral-200">
                                {fullName}
                            </h1>
                            <p className="text-gray-500 dark:text-neutral-500">
                                {email}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="xl:hidden flex justify-end">
                    <button type="button" className="py-1.5 px-2 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-2xs hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-gray-50 dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700" aria-haspopup="dialog" aria-expanded="false" aria-controls="hs-pro-dupsd" aria-label="Sidebar Toggle" data-hs-overlay="#hs-pro-dupsd">
                        <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                            <line x1="15" x2="15" y1="3" y2="21"></line>
                            <path d="m8 9 3 3-3 3"></path>
                        </svg>
                        Buka Sidebar
                    </button>
                </div>
                <div className="xl:p-5 flex flex-col xl:bg-white xl:border xl:border-gray-200 xl:shadow-2xs xl:rounded-xl dark:xl:bg-neutral-800 dark:xl:border-neutral-700">
                    <div className="xl:flex">
                        <div id="hs-pro-dupsd" className="hs-overlay [--auto-close:xl] hs-overlay-open:translate-x-0 -translate-x-full transition-all duration-300 transform w-80 fixed inset-y-0 start-0 z-60 bg-white border-e border-gray-200 xl:relative xl:z-0 xl:block xl:translate-x-0 xl:end-auto xl:bottom-0 overflow-y-auto [&amp;::-webkit-scrollbar]:w-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&amp;::-webkit-scrollbar-track]:bg-neutral-700 dark:[&amp;::-webkit-scrollbar-thumb]:bg-neutral-500 dark:bg-neutral-800 dark:border-neutral-700 hidden" role="dialog" aria-labelledby="hs-pro-dupsd-label">
                            <div className="p-5 xl:p-0">
                                <div className="xl:hidden">
                                    <div className="absolute top-3 end-3 z-10">
                                        <button type="button" className="size-8 shrink-0 flex justify-center items-center gap-x-2 rounded-full border border-transparent bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:text-neutral-400 dark:focus:bg-neutral-600" aria-label="Close" data-hs-overlay="#hs-pro-dupsd" aria-expanded="false">
                                            <span className="sr-only">Close</span>
                                            <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M18 6 6 18"></path>
                                                <path d="m6 6 12 12"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="xl:pe-4 mt-3 divide-y divide-gray-200 dark:divide-neutral-700">
                                    <div className="py-4 first:pt-0 last:pb-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <h2 id="hs-pro-dupsd-label" className="text-sm font-semibold text-gray-800 dark:text-neutral-200">
                                            Tentang
                                        </h2>
                                            {!isEditingProfile ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingProfile(true)}
                                                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                                                >
                                                    Edit Profil
                                                </button>
                                            ) : (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setData({
                                                                name: initialName,
                                                                email: initialEmail,
                                                                phone: initialPhone,
                                                                momo_code: initialMomoCode,
                                                            });
                                                            setIsEditingProfile(false);
                                                        }}
                                                        className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
                                                    >
                                                        Batal
                                                    </button>
                                                    <button
                                                        type="submit"
                                                        form="profile-update-form"
                                                        disabled={processing}
                                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        {processing ? "Sedang memperbarui..." : "Perbarui Profil"}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {!isEditingProfile ? (
                                            <ul className="mt-3 space-y-2.5">
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nama Lengkap</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{fullName}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Email</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200 break-all">{email}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Kode Member</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{memberCode}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nomor Telepon</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{phone}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Kode Momo</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{momoCode}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nama Bank</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{bankName}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nama Rekening</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{bankAccountName}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nomor Rekening</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{bankAccountNumber}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Tanggal Daftar Member</p>
                                                    <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-neutral-200">{registrationMemberDate}</p>
                                                </li>
                                                <li className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Status Membership</p>
                                                    <span className={`mt-1 inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${membershipStatus === "Aktif" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"}`}>
                                                        {membershipStatus}
                                                    </span>
                                                </li>
                                            </ul>
                                        ) : (
                                            <form id="profile-update-form" className="mt-3 space-y-2.5" onSubmit={handleProfileSubmit}>
                                                <div className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <label htmlFor="profile-name" className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nama Lengkap</label>
                                                    <input
                                                        id="profile-name"
                                                        type="text"
                                                        value={data.name}
                                                        onChange={(event) => setData("name", event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
                                                    />
                                                    {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                                                </div>
                                                <div className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <label htmlFor="profile-email" className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Email</label>
                                                    <input
                                                        id="profile-email"
                                                        type="email"
                                                        value={data.email}
                                                        onChange={(event) => setData("email", event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
                                                    />
                                                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                                                </div>
                                                <div className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <label htmlFor="profile-phone" className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Nomor Telepon</label>
                                                    <input
                                                        id="profile-phone"
                                                        type="text"
                                                        value={data.phone}
                                                        onChange={(event) => setData("phone", event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
                                                    />
                                                    {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                                                </div>
                                                <div className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/50">
                                                    <label htmlFor="profile-momo-code" className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">Kode Momo</label>
                                                    <input
                                                        id="profile-momo-code"
                                                        type="text"
                                                        value={data.momo_code}
                                                        onChange={(event) => setData("momo_code", event.target.value)}
                                                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
                                                    />
                                                    {errors.momo_code && <p className="mt-1 text-xs text-red-600">{errors.momo_code}</p>}
                                                </div>
                                                {(flash.success || recentlySuccessful) && (
                                                    <p className="text-xs text-emerald-600">
                                                        {flash.success ?? "Profil berhasil diperbarui."}
                                                    </p>
                                                )}
                                            </form>
                                        )}
                                    </div>

                                    <div className="py-4 first:pt-0 last:pb-0">
                                        <h2 className="text-sm font-semibold text-gray-800 dark:text-neutral-200">
                                            Panel Member
                                        </h2>
                                        <div className="mt-3 space-y-2.5">
                                            {hasMomoCode && (
                                                <div className="rounded-xl border border-card-line bg-card p-5 shadow-2xs">
                                                    <div className="flex items-center gap-4">
                                                        <img src="http://localhost:8000/momo_live.png" alt="Pengguna Member" className="h-16 w-16 rounded-full object-cover border border-layer-line" />
                                                        <div><h3 className="text-base font-semibold text-foreground">Momo Live</h3>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {!hasMomoCode &&
                                                <li className="text-sm text-gray-500 dark:text-neutral-500">
                                                    Tidak ada panel membership.
                                                </li>
                                            }
                                        </div>
                                    </div>

                                    <div className="py-4 first:pt-0 last:pb-0">
                                        <h2 className="mb-3 text-sm font-semibold text-gray-800 dark:text-neutral-200">
                                            Riwayat Membership
                                        </h2>
                                        <ul className="space-y-3">
                                            {memberHistories.length === 0 ? (
                                                <li className="text-sm text-gray-500 dark:text-neutral-500">
                                                    Belum ada riwayat membership.
                                                </li>
                                            ) : (
                                                memberHistories.map((history) => {
                                                    const levelName =
                                                        history?.membership_level?.mem_level_name ??
                                                        "Level Tidak Diketahui";
                                                    const status = getHistoryStatus(history);

                                                    return (
                                                        <li key={history.mem_level_history_id}>
                                                            <div className="rounded-lg border border-gray-200 dark:border-neutral-700 px-3 py-2.5">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <p className="text-sm font-medium text-gray-800 dark:text-neutral-200">
                                                                        {levelName}
                                                                    </p>
                                                                    <span
                                                                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${status === "Aktif"
                                                                            ? "bg-green-100 text-green-700"
                                                                            : status === "Selesai"
                                                                                ? "bg-blue-100 text-blue-700"
                                                                                : "bg-amber-100 text-amber-700"
                                                                            }`}
                                                                    >
                                                                        {status}
                                                                    </span>
                                                                </div>
                                                                <p className="mt-1 text-xs text-gray-500 dark:text-neutral-500">
                                                                    Mulai: {formatDate(history?.start_date)}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-neutral-500">
                                                                    Target Selesai: {formatDate(history?.target_end_date)}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-neutral-500">
                                                                    Selesai Aktual: {formatDate(history?.actual_end_date)}
                                                                </p>
                                                            </div>
                                                        </li>
                                                    );
                                                })
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="xl:ps-5 grow space-y-5">
                            <div className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-2xs xl:shadow-none dark:bg-neutral-800 dark:border-neutral-700">
                                <div className="p-5 border-b border-gray-200 dark:border-neutral-700">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                        <div>
                                            <h2 className="inline-block font-semibold text-lg text-gray-800 dark:text-neutral-200">
                                                Komisi
                                            </h2>
                                            <p className="text-sm text-gray-500 dark:text-neutral-500">
                                                Data komisi member
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                                            <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-neutral-900/50">
                                                <label htmlFor="commission-date-filter" className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Filter Tanggal
                                                </label>
                                                <input
                                                    id="commission-date-filter"
                                                    type="date"
                                                    value={selectedCommissionDate}
                                                    onChange={handleCommissionDateChange}
                                                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200"
                                                />
                                            </div>
                                            <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-neutral-900/50">
                                                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Komisi
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">
                                                    {commissions.length}
                                                </p>
                                            </div>
                                            <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-neutral-900/50">
                                                <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Total Komisi
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 dark:text-neutral-200">
                                                    {formatCurrency(totalCommissionAmount)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                                        <thead className="bg-gray-50 dark:bg-neutral-900/40">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    SO Code
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Produk
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Varian
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Tipe / Rate
                                                </th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Nominal
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-500">
                                                    Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
                                            {commissions.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan="6"
                                                        className="px-4 py-8 text-center text-sm text-gray-500 dark:text-neutral-500"
                                                    >
                                                        Belum ada data komisi.
                                                    </td>
                                                </tr>
                                            ) : (
                                                commissions.map((commission) => {
                                                    const statusMeta = getCommissionStatusMeta(
                                                        Number(commission?.status ?? 0)
                                                    );

                                                    return (
                                                        <tr key={commission.soc_id} className="hover:bg-gray-50/80 dark:hover:bg-neutral-900/40">
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                                                <a
                                                                    href={`/profile/sales-order?soCode=${encodeURIComponent(commission.so_code ?? "")}`}
                                                                    className="text-indigo-600 hover:text-indigo-700 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
                                                                >
                                                                    {commission.so_code ?? "-"}
                                                                </a>
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-neutral-300">
                                                                {commission.product_name ?? "-"}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-neutral-300">
                                                                {commission.variant_name ?? "-"}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-neutral-300">
                                                                <div>{commission.commission_type_label ?? "-"}</div>
                                                                <div className="text-xs text-gray-500 dark:text-neutral-500">
                                                                    Tarif: {Number(commission.commission_rate ?? 0)}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800 dark:text-neutral-200">
                                                                {formatCurrency(commission.commission_amount)}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="space-y-1">
                                                                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusMeta.className}`}>
                                                                        {commission.status_label ?? statusMeta.label}
                                                                    </span>
                                                                    <div className="text-xs text-gray-500 dark:text-neutral-500">
                                                                        {commission.payout_code ? `Batch: ${commission.payout_code}` : `Disetujui: ${formatDate(commission.approved_at ?? commission.created_at)}`}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}
